import { useQuery } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';

import { useAuth } from './useAuth';
import { useEntity } from '../contexts/EntityContext';
import { extractPlatformUser } from '../utils/auth0UserUtils';
import { fetchAllEntitledEntities, fetchUserInfo } from '../services/entityApi';
import { useLoggerWithContext } from '@/services/logger/loggerContext';
import type { Entity } from '../types';

const TWO_MINUTES = 2 * 60 * 1000;
const ONE_HOUR = 60 * 60 * 1000;
const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;

/**
 * The identity traits that are not in the Auth0 token.
 *
 * Six traits come from two APIs rather than from claims: `og_department` and
 * `psp_role_display` from the User API, and `og_country`, `og_region`,
 * `og_city`, `og_salesforceid` from the full Entity record. `useSegmentIdentify`
 * has always fetched them; `useOgAnalytics` was passing `undefined` in their
 * place, so every user on the new SDK had all six empty and any segment keyed
 * on role display name, department, geography or Salesforce ID returned
 * nothing. That is a parity break in the migration this exists to deliver.
 *
 * **The query keys here are deliberately identical to the ones in
 * `useSegmentIdentify`.** React Query dedupes by key, so both hooks share one
 * cache entry and one network request — running the two analytics systems in
 * parallel costs nothing extra, and this file does not have to touch the live
 * Segment path to fix the new one.
 */
export interface AnalyticsSupplementalData {
  department: string | undefined;
  roleDisplayName: string | undefined;
  fullEntity: Entity | null | undefined;
}

export function useAnalyticsSupplementalData(enabled: boolean): AnalyticsSupplementalData {
  const { user } = useAuth();
  const { getAccessTokenSilently } = useAuth0();
  const { activeEntity } = useEntity();
  const logger = useLoggerWithContext();
  const userUuid = extractPlatformUser(user)?.uuid;

  // A failed lookup is logged rather than thrown to a boundary: these traits
  // enrich analytics, and must not take the page down with them. Without the
  // log, a 500 from either API would leave the traits empty with no trace.
  // Rethrown so React Query still retries and records the error state.
  const reportFailure = (lookup: string, err: unknown): never => {
    logger.warn('Analytics trait lookup failed', {
      analytics_lookup: lookup,
      error_message: err instanceof Error ? err.message : String(err),
    });
    throw err;
  };

  // Address and Salesforce ID. `EntityContext` strips entities to `EntityBase`
  // via `extractBaseEntity()`, so this parallel query hits the same
  // (HTTP-cached) endpoint and keeps the full response.
  const { data: fullEntity } = useQuery({
    queryKey: ['analyticsFullEntity', userUuid, activeEntity?.friendlyId],
    queryFn: async () => {
      try {
        const token = await getAccessTokenSilently();
        const entities = await fetchAllEntitledEntities(userUuid!, token, logger);
        return entities.find((e) => e.friendlyId === activeEntity?.friendlyId) ?? null;
      } catch (err) {
        return reportFailure('full_entity', err);
      }
    },
    enabled: enabled && !!userUuid && !!activeEntity,
    staleTime: TWO_MINUTES,
    gcTime: TWENTY_FOUR_HOURS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Department and role display name. `useUserDisplayName` extracts only
  // `full_name` from the same endpoint, so analytics needs the full record.
  const { data: userInfo } = useQuery({
    queryKey: ['user-info', userUuid],
    queryFn: async () => {
      try {
        const token = await getAccessTokenSilently();
        return await fetchUserInfo(userUuid!, token, logger);
      } catch (err) {
        return reportFailure('user_info', err);
      }
    },
    enabled: enabled && !!userUuid,
    staleTime: ONE_HOUR,
    gcTime: ONE_HOUR,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    department: userInfo?.department,
    roleDisplayName: userInfo?.role?.display_name,
    fullEntity,
  };
}
